import { z } from "zod";

export const tabletDiagnosticOperation = z.enum([
  "counter.read",
  "run.start",
  "run.complete",
  "downtime.reason.save",
  "quantity.save",
  "terminal.refresh",
  "terminal.command",
  "production.read",
  "production.write",
]);

const measuredMs = z.number().int().min(0).max(60_000);
export const tabletDiagnosticRequest = z.object({
  clientRequestId: z.uuid(),
  attempt: z.number().int().min(1).max(2),
  responseStatus: z.number().int().min(100).max(599).nullable(),
  durationMs: measuredMs,
  responseHeadersMs: measuredMs.nullable(),
  bodyReadMs: measuredMs.nullable(),
  jsonParseMs: measuredMs.nullable(),
}).strict();

export const tabletDiagnosticEvent = z.object({
  id: z.uuid(),
  occurredAt: z.iso.datetime(),
  operation: tabletDiagnosticOperation,
  outcome: z.enum(["FAILURE", "SLOW", "RECOVERED"]),
  errorCode: z.string().regex(/^[A-Z0-9_]{1,80}$/u).nullable(),
  serverRequestId: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u).nullable(),
  durationMs: z.number().finite().min(0).max(60_000),
  request: tabletDiagnosticRequest.optional(),
  counter: z.object({
    configured: z.boolean(),
    source: z.enum(["LOCAL_PLC_TEST"]).nullable(),
    quality: z.enum(["GOOD", "STALE", "COUNTER_RESET", "BASELINE_MISSING", "GAP_ACROSS_RUN", "NO_DATA"]).nullable(),
    measuredAt: z.iso.datetime().nullable(),
    ageSeconds: z.number().int().min(0).max(86_400).nullable(),
  }).strict().nullable(),
}).strict();

export const tabletDiagnosticBatch = z.object({
  events: z.array(tabletDiagnosticEvent).min(1).max(6),
}).strict();

export const tabletDiagnosticQueue = z.array(tabletDiagnosticEvent).max(24);

export type TabletDiagnosticEvent = z.infer<typeof tabletDiagnosticEvent>;
export type TabletDiagnosticBatch = z.infer<typeof tabletDiagnosticBatch>;

export const tabletDiagnosticBatchByteLimit = 4096;
export function tabletDiagnosticDeliveryBatch(events: readonly TabletDiagnosticEvent[]) {
  const batch: TabletDiagnosticEvent[] = [];
  const encoder = new TextEncoder();
  for (const event of events.slice(0, 6)) {
    const candidate = [...batch, event];
    if (encoder.encode(JSON.stringify({ events: candidate })).byteLength > tabletDiagnosticBatchByteLimit) break;
    batch.push(event);
  }
  return batch;
}
